import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useAgentInterception from '../../../hooks/agentInterception/useAgentInterception.hooks';
import useScopedCartActions from '../../../hooks/cartScope/useScopedCartActions.hooks';
import useItemConversion from '../../../hooks/useItemConversion.hooks';
import useModifierSelectionInfo from '../../../hooks/useModifierSelectionInfo/useModifierSelectionInfo.hooks';
import { ISelectModifierPayload } from '../../../reducers/cartSlice';
import { AgentInterceptionType } from '../../../reducers/messagingSlice';
import { selectIsAgentActionBlocked } from '../../../redux/features/agentMode/agentMode.selector';
import { selectFeatureFlagSuppressUpsell } from '../../../redux/features/config/config.selector';
import { selectWithheldConstraintNames } from '../../../redux/features/experiments/experiments.selector';
import {
  modSymbolMappingSelector,
  posSettingsSelector,
  selectAIUpsellSuppression,
  selectIsAIExpressUpsellSuppressionActive,
  selectIsExpressTimerActive,
  selectIsExpressUpsellMode,
  selectUnavailableItemsByName,
} from '../../../selectors/menu';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { CartItem } from '../../../utils/cart';
import logger from '../../../utils/logger';

/**
 * One slot of a repeated-slot set changing value. `to` undefined clears the
 * slot; `from` undefined means it was empty.
 */
export interface RepeatedSlotChange {
  modGroup: ParsedModifierGroup;
  /** The slot's cart path; its group id when omitted (a top-level slot). */
  pathToItem?: string;
  from?: ParsedMenuItem;
  to?: ParsedMenuItem;
}

/**
 * The repeated-slot panel's write path: one gesture (tap, paint stroke, Fill
 * all, Undo) is ONE `selectModifiers` dispatch, so one cart change and one
 * `order` — where 13 single picks would send 13 of each.
 *
 * It applies the rules of `CartItemDetail`'s `onMenuItemModifierSelect`, once
 * per batch instead of once per pick:
 * - writes go through `useScopedCartActions`, the same hook the pane uses, so a
 *   My Cart line writes to `draftCart` and the Live Order to `cart`. Never call
 *   `cartActions` directly: an omitted scope defaults to main (HITL-1715);
 * - a main-cart write outside Intercept is refused whole (HITL-1612/1617);
 * - an 86'd option is never written. The tile cannot be tapped, so there is no
 *   customer line to send either;
 * - a draft write emits nothing — no metric, no intervention — because nothing
 *   in a draft has been ordered;
 * - the metric stays per slot, but the intervention is ONE event carrying
 *   `summary`, so the AI hears "6 Plain, 4 Everything, 3 Sesame" once rather
 *   than "Plain added to order" thirteen times.
 *
 * Returns whether anything was written.
 */
const useRepeatedSlotsWrite = (cartItem: CartItem) => {
  const dispatch = useDispatch();
  const {
    scope: writeScope,
    actions: cartWriteActions,
    isDraft,
  } = useScopedCartActions();
  const { handleModifierSelection } = useItemConversion(writeScope);
  const { sendAgentInterception } = useAgentInterception();
  const { sendModifierSelectionInfo } = useModifierSelectionInfo();
  const isAgentActionBlocked = useSelector(selectIsAgentActionBlocked);
  const modSymbolMapping = useSelector(modSymbolMappingSelector);
  const posSettings = useSelector(posSettingsSelector);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const suppressUpsellEnabled = useSelector(selectFeatureFlagSuppressUpsell);
  const expressUpsellMode = useSelector(selectIsExpressUpsellMode);
  const expressTimerActive = useSelector(selectIsExpressTimerActive);
  // HITL-1799: the AI's own express mode and decline-count rules.
  const aiExpressActive = useSelector(selectIsAIExpressUpsellSuppressionActive);
  const aiUpsellSuppression = useSelector(selectAIUpsellSuppression);
  const withheldConstraints = useSelector(selectWithheldConstraintNames);

  return useCallback(
    (changes: RepeatedSlotChange[], summary: string): boolean => {
      if (isAgentActionBlocked && !isDraft) {
        logger.debug({
          message: `Repeated-slot write on "${cartItem.name}" refused: the agent is not in Intercept mode`,
        });
        return false;
      }

      const applicable = changes.filter(
        ({ from, to }) =>
          from?.itemId !== to?.itemId && (to ? to.available !== false : !!from)
      );
      if (!applicable.length) return false;

      const toPayload = ({
        modGroup,
        pathToItem,
        from,
        to,
      }: RepeatedSlotChange): ISelectModifierPayload => ({
        cartItemId: cartItem.cartItemId,
        // A single-pick group replaces on select, so painting over a filled
        // slot is a plain select; only clearing names the old option.
        menuItemId: (to || from)!.itemId,
        modGroupId: modGroup.id,
        selected: !!to,
        modCode: '',
        modSymbolMapping,
        // The slot's own path, so a slot below the top level
        // (`slot__option__group`) is written where RecursiveModGroup would.
        pathToItem: pathToItem ?? modGroup.id,
        posSettings,
        readback: true,
        applySuppressCascade: true,
        suppressUpsellEnabled,
        applyExpressCascade:
          expressUpsellMode || expressTimerActive || aiExpressActive,
        withheldConstraints,
        aiUpsellSuppression,
      });

      dispatch(
        cartWriteActions.selectModifiers({
          selections: applicable.map((change) => ({
            selection: toPayload(change),
          })),
          unavailableItemsByName,
        })
      );

      if (!isDraft) {
        applicable.forEach(({ modGroup, from, to }) =>
          sendModifierSelectionInfo({
            menuItem: (to || from)!,
            modGroup,
            selected: !!to,
          })
        );
        sendAgentInterception({
          data: {
            type: AgentInterceptionType.ORDER_CHANGED,
            description: summary,
          },
        });
      }

      // Parity with the single pick, which runs item conversion after every
      // agent selection. Bagel options convert nothing, but a set on some
      // other item might, and skipping it would differ silently.
      applicable.forEach(({ modGroup, from, to }) =>
        handleModifierSelection(modGroup, (to || from)!.name, cartItem)
      );

      return true;
    },
    [
      cartItem,
      cartWriteActions,
      dispatch,
      expressTimerActive,
      expressUpsellMode,
      aiExpressActive,
      aiUpsellSuppression,
      handleModifierSelection,
      isAgentActionBlocked,
      isDraft,
      modSymbolMapping,
      posSettings,
      sendAgentInterception,
      sendModifierSelectionInfo,
      suppressUpsellEnabled,
      unavailableItemsByName,
      withheldConstraints,
    ]
  );
};

export default useRepeatedSlotsWrite;
